import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  MenuItem,
  Paper,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../api";
import { issueDocumentPdf } from "../../lib/documentPdf";

/* =========================
 * Component
 * ========================= */
export default function InvoiceCreateDialog({
  open,
  onClose,
  customer,
  onSubmit,
  mode = "create",
  initialData = null,
}) {

  /* -------------------------
   * 基本情報
   * ------------------------- */
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [taxInclusive, setTaxInclusive] = useState(false);
  const isEdit = mode === "edit";
  const isCreateWithInitial = !isEdit && !!initialData;
  const [fixedCustomer, setFixedCustomer] = useState(null);

  /* -------------------------
   * プロジェクト選択
   * ------------------------- */
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);

  /* -------------------------
   * 明細
   * ------------------------- */
  const [items, setItems] = useState([]);

  /* =========================
   * 初期化
   * ========================= */
  useEffect(() => {

    if (!open) return;

    if (customer?.id) {
      setFixedCustomer(customer);
    }

    setProjects([]);

    (async () => {
      setLoadingProjects(true);
      try {
        const res = await api.get(`/api/projects/by-customer/${customer.id}`);
        setProjects(res.data || []);
      } finally {
        setLoadingProjects(false);
      }
    })();

    const today = new Date().toISOString().slice(0, 10);

    // --- 編集 ---
    if (isEdit && initialData) {
      setInvoiceDate(initialData.issueDate?.slice(0, 10) || "");
      setDueDate(initialData.dueDate?.slice(0, 10) || "");
      setTitle(initialData.title || "");
      setNotes(initialData.note || "");
      setTaxInclusive(false);
      setProjectId(initialData.projectId || "");

      setItems(
        (initialData.items || []).map((x, i) => ({
          id: i + 1,
          description: x.description,
          quantity: x.quantity,
          unit: x.unit,
          unitPrice: x.unitPrice,
          taxRate: x.taxRate,
          amount: x.amount,
        }))
      );
      return;
    }

    // --- 新規（案件起点・複製起点 両対応） ---
    if (isCreateWithInitial) {
      setInvoiceDate(initialData.issueDate || today);
      setDueDate(initialData.dueDate || "");
      setTitle(initialData.title || "");
      setNotes(initialData.note || "");
      setTaxInclusive(false);
      setProjectId(initialData.projectId || "");

      setItems(
        (initialData.items || []).map((x, i) => ({
          id: i + 1,
          description: x.description,
          quantity: x.quantity,
          unit: x.unit,
          unitPrice: x.unitPrice,
          taxRate: x.taxRate,
          amount: x.amount,
        }))
      );

      // items が無い場合だけ 1 行追加
      if (!initialData.items || initialData.items.length === 0) {
        setItems([newItem(1)]);
      }

      return;
    }

    // --- 完全新規 ---
    setInvoiceDate(today);
    setDueDate("");
    setTitle("");
    setNotes("");
    setTaxInclusive(false);
    setItems([newItem(1)]);
    setProjectId("");
  }, [open, customer, isEdit, initialData]);




  /* =========================
   * 金額計算
   * ========================= */
  const { subtotal, taxTotal, total: autoTotal } = useMemo(
    () => calcTotals(items, taxInclusive),
    [items, taxInclusive]
  );
  // xlsx 行19: 合計金額の自由書き換え。null なら自動計算値を使う
  const [manualTotal, setManualTotal] = useState(null);
  const total = manualTotal != null ? manualTotal : autoTotal;

  /* =========================
   * 明細操作
   * ========================= */
  const addItem = () => {
    setItems((prev) => [...prev, newItem(nextId(prev))]);
  };

  const deleteItem = (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const patchItem = (id, patch) => {
    setItems((prev) =>
      prev.map((x) => {
        if (x.id !== id) return x;
        const merged = { ...x, ...patch };
        const quantity = toNum(merged.quantity);
        const unitPrice = toNum(merged.unitPrice);
        const taxRate = toNum(merged.taxRate);
        const amount = Math.round(quantity * unitPrice);

        return {
          ...merged,
          quantity,
          unitPrice,
          taxRate,
          amount,
        };
      })
    );
  };

  /* =========================
   * DataGrid（表示専用）
   * ========================= */
  const gridRows = items.map((x) => ({
    ...x,
    unitPriceLabel: formatJPY(x.unitPrice),
    amountLabel: formatJPY(x.amount),
    taxRateLabel: `${x.taxRate}%`,
  }));

  const columns = [
    { field: "id", headerName: "#", width: 56 },
    { field: "description", headerName: "科目 / 説明", flex: 1.2 },
    { field: "quantity", headerName: "数量", width: 80 },
    { field: "unit", headerName: "単位", width: 80 },
    { field: "unitPriceLabel", headerName: "単価", width: 120 },
    { field: "taxRateLabel", headerName: "税率", width: 80 },
    { field: "amountLabel", headerName: "金額", width: 120 },
    {
      field: "actions",
      headerName: "",
      width: 56,
      renderCell: (p) => (
        <IconButton onClick={() => deleteItem(p.row.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  /* =========================
   * 保存
   * ========================= */
  const [saving, setSaving] = useState(false);
  const [itemError, setItemError] = useState(false);
  const [formError, setFormError] = useState("");

  const handleSave = async () => {
    if (!projectId) {
      setFormError("プロジェクトを選択してください");
      return;
    }
    // 何も入力されていない行は保存対象から外す
    const filled = items.filter(
      (x) => (x.description || "").trim() !== "" || toNum(x.unitPrice) !== 0 || toNum(x.amount) !== 0
    );

    if (filled.length === 0) {
      setItemError(true);
      setFormError("明細が未入力です。科目 / 説明と単価を入力してください。この内容では保存されません。");
      return;
    }

    const missingLabel = filled.find((x) => (x.description || "").trim() === "");
    if (missingLabel) {
      setItemError(true);
      setFormError(`行 #${missingLabel.id} の「科目 / 説明」が未入力です。入力してから発行してください。この内容では保存されません。`);
      return;
    }

    setItemError(false);
    setFormError("");

    const payload = {
      customer_id: customer.id,
      project_id: projectId,
      invoice: {
        invoice_date: invoiceDate,
        due_date: dueDate || null,
        title,
        tax_inclusive: taxInclusive,
        notes,
      },
      items: filled.map((x) => ({
        description: x.description.trim(),
        quantity: x.quantity,
        unit: x.unit,
        unit_price: x.unitPrice,
        tax_rate: x.taxRate,
        amount: x.amount,
      })),
      totals: { subtotal, taxTotal, total },
    };

    // 登録 → PDF 発行まで完了してから閉じる
    setSaving(true);
    try {
      const { data: saved } = isEdit
        ? await api.post(`/api/invoices/${initialData.id}/revise`, payload)
        : await api.post("/api/invoices", payload);

      await issueDocumentPdf("invoices", saved?.id);

      if (onSubmit) await onSubmit(saved, payload);
      onClose();
    } catch (e) {
      console.error("[請求書発行エラー]", e);
      setFormError(`請求書の発行に失敗しました: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };


  /* =========================
   * Render
   * ========================= */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        請求書発行（{customer?.name ?? "顧客未選択"}）
      </DialogTitle>

            <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError("")}>
            {formError}
          </Alert>
        )}

        {/* =====================
       * 基本情報
       * ===================== */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            基本情報
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="請求日"
                type="date"
                fullWidth
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="支払期日"
                type="date"
                fullWidth
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="請求書番号"
                fullWidth
                disabled
                value="（自動発行）"
              />
            </Grid>

          </Grid>
          <Grid container spacing={2} paddingTop={2}>

            {/* 件名＋プロジェクト（同一行） */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="件名"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {projects.length > 0 ? (
                <TextField
                  select
                  required
                  label="プロジェクト"
                  fullWidth
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.title}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Box>
                  <Typography color="error" sx={{ mb: 1 }}>
                    このクライアントに紐づくプロジェクトがありません
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() =>
                    (window.location.href =
                      `/projects/new?customerId=${customer.id}`)
                    }
                  >
                    プロジェクトを作成
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* =====================
       * 明細
       * ===================== */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1">明細</Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={taxInclusive}
                    onChange={(e) => setTaxInclusive(e.target.checked)}
                  />
                }
                label={taxInclusive ? "税込入力" : "税抜入力"}
              />

              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={addItem}
              >
                科目を追加
              </Button>
            </Box>
          </Box>

          <Paper variant="outlined" sx={{ p: 1 }}>
            <Box sx={{ height: 240 }}>
              <DataGrid
                rows={gridRows}
                columns={columns}
                hideFooter
                density="compact"
                disableRowSelectionOnClick
              />
            </Box>

            {/* 行別入力UI（既存） */}
            {items.map((x) => (
              <Box key={x.id} sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  行 #{x.id}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="科目 / 説明"
                      fullWidth
                      required
                      error={itemError && (x.description || "").trim() === ""}
                      helperText={
                        itemError && (x.description || "").trim() === ""
                          ? "項目名を入力してください"
                          : " "
                      }
                      value={x.description}
                      onChange={(e) =>
                        patchItem(x.id, { description: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      label="数量"
                      type="number"
                      fullWidth
                      value={x.quantity}
                      onChange={(e) =>
                        patchItem(x.id, { quantity: toNum(e.target.value) })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      label="単位"
                      fullWidth
                      value={x.unit}
                      onChange={(e) =>
                        patchItem(x.id, { unit: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      label="単価"
                      type="number"
                      fullWidth
                      value={x.unitPrice}
                      onChange={(e) =>
                        patchItem(x.id, { unitPrice: toNum(e.target.value) })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      label="税率"
                      select
                      fullWidth
                      value={x.taxRate}
                      onChange={(e) =>
                        patchItem(x.id, { taxRate: toNum(e.target.value) })
                      }
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={8}>8%</MenuItem>
                      <MenuItem value={10}>10%</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="金額（自動）"
                      fullWidth
                      value={formatJPY(x.amount)}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 3,
                mt: 2,
              }}
            >
              <Summary label="小計" value={subtotal} />
              <Summary label="消費税" value={taxTotal} />
              {/* xlsx 行19: 合計を編集可能。空にすると自動計算値に戻る */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="caption" color="text.secondary">合計</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={manualTotal != null ? manualTotal : autoTotal}
                  onChange={(e) => {
                    const v = e.target.value;
                    setManualTotal(v === '' ? null : Number(v));
                  }}
                  inputProps={{ style: { textAlign: 'right', fontWeight: 'bold', width: 140 } }}
                />
                {manualTotal != null && manualTotal !== autoTotal && (
                  <Typography variant="caption" color="warning.main">
                    自動計算: ¥{autoTotal.toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* =====================
       * 備考（明細の下）
       * ===================== */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            備考
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>

      </DialogContent>

      <Divider />

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>キャンセル</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "発行中..." : "発行してPDFを取得"}
        </Button>
      </DialogActions>
    </Dialog>
  );

}

/* =========================
 * helpers
 * ========================= */

function newItem(id) {
  return {
    id,
    description: "",
    quantity: 1,
    unit: "式",
    unitPrice: 0,
    taxRate: 10,
    amount: 0,
  };
}

function nextId(list) {
  return list.length ? Math.max(...list.map((x) => x.id)) + 1 : 1;
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatJPY(v) {
  return (Number(v) || 0).toLocaleString("ja-JP", {
    style: "currency",
    currency: "JPY",
  });
}

function calcTotals(items, taxInclusive) {
  let subtotal = 0;
  let taxTotal = 0;

  for (const x of items) {
    const rate = (x.taxRate || 0) / 100;
    const line = x.amount || 0;

    if (taxInclusive && rate > 0) {
      const base = line / (1 + rate);
      subtotal += base;
      taxTotal += line - base;
    } else {
      subtotal += line;
      taxTotal += line * rate;
    }
  }

  return {
    subtotal: Math.round(subtotal),
    taxTotal: Math.round(taxTotal),
    total: Math.round(subtotal + taxTotal),
  };
}

function Summary({ label, value, bold }) {
  return (
    <Typography variant={bold ? "h6" : "body1"} fontWeight={bold ? 700 : 400}>
      {label}: {formatJPY(value)}
    </Typography>
  );
}
